import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Annotation } from "../../types";

interface Props {
  annotations: Annotation[];
  onUpdate: (id: number, payload: Partial<Annotation>) => void;
  onDelete: (id: number) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

const SEVERITY_OPTIONS = ["blocker", "high", "medium", "low"];
const STATUS_OPTIONS = [
  { value: "needs_attention", label: "Needs attention" },
  { value: "resolved", label: "Resolved" },
  { value: "positive", label: "Positive" },
  { value: "informational", label: "Informational" }
];

interface DraftState {
  id: number;
  severity: string;
  failureMode: string;
  note: string;
  resolutionNote: string;
  status: string;
}

export default function AnnotationList({ annotations, onUpdate, onDelete, isUpdating, isDeleting }: Props) {
  const sorted = useMemo(
    () => [...annotations].sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "")),
    [annotations]
  );

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);

  const activeAnnotation = useMemo(
    () => (editingId == null ? null : sorted.find((annotation) => annotation.id === editingId) ?? null),
    [editingId, sorted]
  );

  useEffect(() => {
    if (!activeAnnotation) {
      setDraft(null);
      return;
    }
    setDraft((prev) => {
      if (prev?.id === activeAnnotation.id) {
        return prev;
      }
      return buildDraftFromAnnotation(activeAnnotation);
    });
  }, [activeAnnotation]);

  useEffect(() => {
    if (!editingId) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setEditingId(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [editingId]);

  if (!sorted.length) {
    return <p className="muted">No annotations yet. Add the first note to capture failure modes.</p>;
  }

  return (
    <>
      <ul className="annotation-list">
        {sorted.map((annotation) => {
          const status = normalizeStatus(annotation.status);
          const isResolved = status === "resolved";
          const statusLabel = formatStatus(status);
          const isActive = editingId === annotation.id;

          return (
            <li key={annotation.id}>
              <AnnotationCard
                annotation={annotation}
                statusLabel={statusLabel}
                isResolved={isResolved}
                isActive={isActive}
                onResolveToggle={(nextStatus) => {
                  onUpdate(annotation.id, { status: nextStatus });
                  if (nextStatus === "resolved") {
                    const nextDraft = buildDraftFromAnnotation(annotation);
                    nextDraft.status = "resolved";
                    setDraft(nextDraft);
                    setEditingId(annotation.id);
                  }
                }}
                onEdit={() => setEditingId(annotation.id)}
                onDelete={() => confirmDeletion(annotation.id, onDelete)}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
              />
            </li>
          );
        })}
      </ul>

      {activeAnnotation && draft && editingId !== null && (
        <AnnotationModal onDismiss={() => setEditingId(null)}>
          <header className="annotation-modal__header">
            <div>
              <h4>Edit annotation</h4>
              <span className="muted">Trace #{activeAnnotation.traceId}</span>
            </div>
            <button type="button" className="annotation-modal__close" onClick={() => setEditingId(null)}>
              Close
            </button>
          </header>
          <AnnotationEditForm
            draft={draft}
            onChange={setDraft}
            onCancel={() => setEditingId(null)}
            onDelete={() => confirmDeletion(activeAnnotation.id, onDelete)}
            onSubmit={(payload) => {
              onUpdate(activeAnnotation.id, payload);
              setEditingId(null);
            }}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
        </AnnotationModal>
      )}
    </>
  );
}

function AnnotationCard({
  annotation,
  statusLabel,
  isResolved,
  isActive,
  onResolveToggle,
  onEdit,
  onDelete,
  isUpdating,
  isDeleting
}: {
  annotation: Annotation;
  statusLabel: string;
  isResolved: boolean;
  isActive: boolean;
  onResolveToggle: (nextStatus: "needs_attention" | "resolved") => void;
  onEdit: () => void;
  onDelete: () => void;
  isUpdating: boolean;
  isDeleting: boolean;
}) {
  const cardClasses = [
    "annotation-card",
    isResolved ? "annotation-card--resolved" : "",
    isActive ? "annotation-card--active" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={cardClasses}>
      <header className="annotation-card__header">
        <div className="annotation-card__title">
          <strong>{annotation.failureMode?.trim() || "Untitled annotation"}</strong>
          <span className="annotation-card__meta">
            {formatTimestamp(annotation.createdAt)}
            {annotation.reviewer ? ` • ${annotation.reviewer}` : ""}
          </span>
        </div>
        <div className="annotation-card__badges">
          {getSeverity(annotation) && <span className={`pill pill--${getSeverity(annotation)}`}>{getSeverity(annotation)}</span>}
          {statusLabel && (
            <span className={isResolved ? "pill pill--outline" : "pill"}>{statusLabel}</span>
          )}
        </div>
      </header>
      <p className={annotation.note ? "annotation-card__note" : "annotation-card__note annotation-card__note--empty"}>
        {annotation.note?.trim() || "No additional notes provided."}
      </p>
      {annotation.resolutionNote && (
        <p className="annotation-card__resolution">
          <strong>Resolve via change:</strong> {annotation.resolutionNote}
        </p>
      )}
      <footer className="annotation-card__footer">
        <div className="annotation-card__footer-meta">
          <span className="muted">Trace #{annotation.traceId}</span>
        </div>
        <div className="annotation-card__actions">
          <button type="button" disabled={isUpdating} onClick={() => onResolveToggle(isResolved ? "needs_attention" : "resolved")}>
            {isResolved ? "Reopen" : "Mark resolved"}
          </button>
          <button type="button" onClick={onEdit}>
            Edit
          </button>
          <button type="button" className="danger" disabled={isDeleting} onClick={onDelete}>
            Delete
          </button>
        </div>
      </footer>
    </article>
  );
}

function AnnotationModal({ children, onDismiss }: { children: ReactNode; onDismiss: () => void }) {
  return (
    <div className="annotation-modal" role="dialog" aria-modal="true" onClick={onDismiss}>
      <div className="annotation-modal__backdrop" />
      <div
        className="annotation-modal__content"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
}

function AnnotationEditForm({
  draft,
  onChange,
  onCancel,
  onDelete,
  onSubmit,
  isUpdating,
  isDeleting
}: {
  draft: DraftState;
  onChange: (draft: DraftState) => void;
  onCancel: () => void;
  onDelete: () => void;
  onSubmit: (payload: Partial<Annotation>) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}) {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      severity: draft.severity,
      failureMode: draft.failureMode || undefined,
      note: draft.note || undefined,
      resolutionNote: draft.resolutionNote || undefined,
      status: draft.status
    });
  };

  return (
    <form className="annotation-card__form" onSubmit={handleSubmit}>
      <div className="annotation-card__form-grid">
        <label>
          <span>Severity</span>
          <select
            value={draft.severity}
            onChange={(event) => onChange({ ...draft, severity: event.target.value })}
          >
            {SEVERITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={draft.status} onChange={(event) => onChange({ ...draft, status: event.target.value })}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        <span>Failure mode</span>
        <input
          type="text"
          value={draft.failureMode}
          onChange={(event) => onChange({ ...draft, failureMode: event.target.value })}
          placeholder="Describe the failure succinctly"
        />
      </label>
      <label>
        <span>Notes</span>
        <textarea
          value={draft.note}
          onChange={(event) => onChange({ ...draft, note: event.target.value })}
          placeholder="Add any supporting context for future reviewers"
        />
      </label>
      {draft.status === "resolved" && (
        <label>
          <span>Resolve via change (optional)</span>
          <textarea
            value={draft.resolutionNote}
            onChange={(event) => onChange({ ...draft, resolutionNote: event.target.value })}
            placeholder="What change shipped to close this issue?"
          />
        </label>
      )}
      <div className="annotation-card__action-row">
        <button type="button" className="danger" disabled={isDeleting} onClick={onDelete}>
          Delete annotation
        </button>
        <div className="annotation-card__action-row--end">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={isUpdating}>
            {isUpdating ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </form>
  );
}

function getSeverity(annotation: Annotation): string | undefined {
  return annotation.severity?.toString();
}

function normalizeStatus(status?: string): string | undefined {
  if (!status) return "needs_attention";
  if (status === "open") return "needs_attention";
  return status;
}

function formatStatus(status?: string): string {
  switch (status) {
    case "needs_attention":
      return "Needs attention";
    case "resolved":
      return "Resolved";
    case "positive":
      return "Positive";
    case "informational":
      return "Informational";
    default:
      return status ?? "";
  }
}

function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return "";
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch (error) {
    console.warn("Failed to parse timestamp", timestamp, error);
    return timestamp;
  }
}

function buildDraftFromAnnotation(annotation: Annotation): DraftState {
  return {
    id: annotation.id,
    severity: annotation.severity ?? SEVERITY_OPTIONS[1] ?? "high",
    failureMode: annotation.failureMode ?? "",
    note: annotation.note ?? "",
    resolutionNote: annotation.resolutionNote ?? "",
    status: normalizeStatus(annotation.status) ?? "needs_attention"
  };
}

function confirmDeletion(id: number, onDelete: (id: number) => void) {
  const acknowledged = window.confirm("Delete this annotation? This action cannot be undone.");
  if (acknowledged) {
    onDelete(id);
  }
}
